import { Product, ProductStoreRequire, ProductStoreRequireAdd, ProductStoreRequireCodeEnum } from "@/types/product";
import { Form, Input, InputNumber, Modal, notification, Select } from "antd";
import { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { productReqAdd, productReqUpdate } from "@/apis/product";
import useSourceType from "@/hooks/useSourceType";
import ProductDesc from "@/components/Desc/ProductDesc";

interface ProductStoreReqModalProps {
  product: Product;
  detail?: ProductStoreRequire | null;
  open: boolean;
  onSuccess: (data: ProductStoreRequire) => void;
  onClose: () => void;
}

const ProductStoreReqModal: FC<ProductStoreReqModalProps> = ({ product, detail, open, onSuccess, onClose }) => {
  const { t } = useTranslation();
  const { productStoreCodeList } = useSourceType();
  const [inForm] = Form.useForm<ProductStoreRequireAdd>();
  const [loading, setLoading] = useState(false);

  const isEdit = useMemo(() => !!detail?.id, [detail]);

  useEffect(() => {
    if (open) {
      if (detail) {
        inForm.setFieldsValue({
          storageCode: detail.storageCode,
          minValue: detail.minValue,
          maxValue: detail.maxValue,
          unit: detail.unit,
          inspectionCycleDays: detail.inspectionCycleDays,
          remark: detail.remark,
        });
      } else {
        inForm.resetFields();
      }
    }
  }, [open, detail, inForm]);

  const handleFinish = async (values: ProductStoreRequireAdd) => {
    setLoading(true);
    try {
      const data = isEdit
        ? await productReqUpdate(product.id, detail!.id, values)
        : await productReqAdd(product.id, values);
      notification.success({ placement: 'top', message: t(isEdit ? "common.updateSuccess" : "common.addSuccess") });
      onSuccess(data);
      onClose();
    } catch (err: any) {
      let desc = '';
      if (err === 70608) desc = t('inspection.configExsist')
      notification.error({ message: t('common.saveFAil'), description: desc })
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      title={t(isEdit ? "product.modify" : "common.add")}
      confirmLoading={loading}
      onOk={() => inForm.submit()}
      onCancel={onClose}
      destroyOnClose
      maskClosable={false}
      width={800}
    >
      <Form form={inForm} name="ProductStoreRequireAdd" labelAlign="left" autoComplete="off" onFinish={handleFinish}>
        <Form.Item noStyle>
          <ProductDesc product={product} />
        </Form.Item>
        <Form.Item<ProductStoreRequireAdd>
          label={t("product.storageCode")}
          name="storageCode"
          rules={[{ required: true, message: `${t("common.plSelect")}${t("product.storageCode")}` }]}
        >
          <Select options={productStoreCodeList} placeholder={t("common.plSelect")} />
        </Form.Item>
        <Form.Item<ProductStoreRequireAdd> label={t("product.minValue")} name="minValue">
          <InputNumber changeOnWheel style={{ width: "100%" }} min={0} />
        </Form.Item>
        <Form.Item<ProductStoreRequireAdd> label={t("product.maxValue")} name="maxValue">
          <InputNumber changeOnWheel style={{ width: "100%" }} min={0} />
        </Form.Item>
        <Form.Item<ProductStoreRequireAdd>
          label={t("product.unit")}
          name="unit"
          rules={[{ required: true, message: t("product.plFill") }]}
        >
          <Input />
        </Form.Item>
        <Form.Item<ProductStoreRequireAdd> label={t("product.inspectionCycleDays")} name="inspectionCycleDays">
          <InputNumber changeOnWheel style={{ width: "100%" }} min={0} precision={0} />
        </Form.Item>
        <Form.Item<ProductStoreRequireAdd> label={t("order.remark")} name="remark">
          <Input.TextArea rows={3} maxLength={500} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ProductStoreReqModal;
